import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, CheckCircle, Code, Clock, Receipt, Palette, ExternalLink, Trash2, AlertTriangle, Package, Wallet, ArrowDownCircle, CreditCard, Ban, Zap, Mail, Calendar, Send, AlertCircle, Bell, BellRing, MessageSquare, ShoppingCart, CreditCard as PaymentIcon, XCircle, Users, Timer, Smartphone, UserPlus, Shield, Eye, Settings2, FileText } from "lucide-react";
import { EmailPreviewDialog } from "@/components/EmailPreviewDialog";
import { InvoiceSettingsForm } from "@/components/professional/InvoiceSettingsForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";
import { format, formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, RefreshCw } from "lucide-react";

interface MerchantPreferencesProps {
  merchantId: string;
  storeDomain: string;
  canEdit: boolean;
  currentPlan?: string;
  onNavigateToBilling?: () => void;
  merchantEmail?: string | null;
  isSuperAdmin?: boolean;
  platformType?: string;
  terminology?: Record<string, string>;
}

interface PreferenceData {
  store_name: string | null;
  email_store_name: string | null;
  default_deadline_hours: number | null;
  default_tax_rate: number | null;
  widget_installed: boolean | null;
  widget_position: string | null;
  brand_color: string | null;
  auto_enable_new_products: boolean | null;
  cancellation_fee_type: 'fixed' | 'percentage' | null;
  cancellation_fee_value: number | null;
  currency: string;
}

interface UninstallStats {
  totalRequests: number;
  completedRequests: number;
  pendingRequests: number;
  totalRevenue: number;
  pendingRevenue: number;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
}));

function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

interface ReportPreferences {
  id?: string;
  email_reports_enabled: boolean;
  report_frequency: "weekly" | "monthly";
  report_day: number;
  report_email: string | null;
  last_report_sent_at: string | null;
}

interface NotificationPreferences {
  id?: string;
  notify_new_order: boolean;
  notify_payment_received: boolean;
  notify_order_completed: boolean;
  notify_order_expired: boolean;
  notify_refund_requested: boolean;
  notify_low_participation: boolean;
  digest_mode: 'instant' | 'daily' | 'weekly';
  digest_time: string;
  notification_email: string | null;
  notification_phone: string | null;
  whatsapp_enabled: boolean;
  reminder_enabled: boolean;
  daily_reminder_enabled: boolean;
  one_hour_reminder_enabled: boolean;
  additional_reminder_enabled: boolean;
  additional_reminder_hours: number;
}

const MerchantPreferences = ({ merchantId, storeDomain, canEdit, currentPlan, onNavigateToBilling, merchantEmail, isSuperAdmin = false, platformType, terminology }: MerchantPreferencesProps) => {
  const isProfessional = platformType === 'professional' || platformType === 'standalone';
  const navigate = useNavigate();
  
  // Professional terminology labels
  const storeLabel = terminology?.store 
    ? terminology.store.charAt(0).toUpperCase() + terminology.store.slice(1) 
    : isProfessional ? "Business" : "Store";
  const customersLabel = terminology?.customers 
    ? terminology.customers.charAt(0).toUpperCase() + terminology.customers.slice(1) 
    : isProfessional ? "Clients" : "Customers";
  const ownerLabel = terminology?.owner 
    ? terminology.owner.charAt(0).toUpperCase() + terminology.owner.slice(1) 
    : isProfessional ? "Business Owner" : "Store Owner";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [installingWidget, setInstallingWidget] = useState(false);
  const [uninstallingWidget, setUninstallingWidget] = useState(false);
  const [syncingMetafield, setSyncingMetafield] = useState(false);
  const [showUninstallDialog, setShowUninstallDialog] = useState(false);
  const [uninstallStats, setUninstallStats] = useState<UninstallStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [sendingTestReport, setSendingTestReport] = useState(false);
  const [sendingTestReminder, setSendingTestReminder] = useState<'midpoint' | 'final' | null>(null);
  const [savingReportPrefs, setSavingReportPrefs] = useState(false);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);
  
  // Team state
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; user_id: string; role: string; created_at: string }>>([]);
  const [invitations, setInvitations] = useState<Array<{ id: string; email: string; role: string; status: string; created_at: string; expires_at: string }>>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("view_only");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<PreferenceData>({
    store_name: null,
    email_store_name: null,
    default_deadline_hours: 72,
    default_tax_rate: 0,
    widget_installed: false,
    widget_position: 'image-top-left',
    brand_color: '#8B5CF6',
    auto_enable_new_products: false,
    cancellation_fee_type: 'fixed',
    cancellation_fee_value: 100,
    currency: 'USD',
  });

  const [reportPreferences, setReportPreferences] = useState<ReportPreferences>({
    email_reports_enabled: true,
    report_frequency: "weekly",
    report_day: 1,
    report_email: null,
    last_report_sent_at: null,
  });

  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    notify_new_order: true,
    notify_payment_received: true,
    notify_order_completed: true,
    notify_order_expired: true,
    notify_refund_requested: true,
    notify_low_participation: false,
    digest_mode: 'instant',
    digest_time: '09:00',
    notification_email: null,
    notification_phone: null,
    whatsapp_enabled: false,
    reminder_enabled: true,
    daily_reminder_enabled: false,
    one_hour_reminder_enabled: true,
    additional_reminder_enabled: false,
    additional_reminder_hours: 12,
  });

  useEffect(() => {
    fetchPreferences();
    fetchTeamData();
  }, [merchantId]);

  const fetchPreferences = async () => {
    try {
      // Fetch merchant preferences
      const { data, error } = await supabase
        .from('merchants')
        .select('store_name, email_store_name, default_deadline_hours, default_tax_rate, widget_installed, widget_position, brand_color, auto_enable_new_products, cancellation_fee_type, cancellation_fee_value, currency')
        .eq('id', merchantId)
        .single();

      if (!error && data) {
        setPreferences({
          store_name: data.store_name ?? null,
          email_store_name: data.email_store_name ?? null,
          default_deadline_hours: data.default_deadline_hours ?? 72,
          default_tax_rate: data.default_tax_rate ?? 0,
          widget_installed: data.widget_installed ?? false,
          widget_position: data.widget_position ?? 'image-top-left',
          brand_color: data.brand_color ?? '#8B5CF6',
          auto_enable_new_products: data.auto_enable_new_products ?? false,
          cancellation_fee_type: (data.cancellation_fee_type as 'fixed' | 'percentage') ?? 'fixed',
          cancellation_fee_value: data.cancellation_fee_value ?? 100,
          currency: data.currency ?? 'USD',
        });
      }

      // Fetch report preferences
      const { data: reportData, error: reportError } = await supabase
        .from("merchant_report_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      if (!reportError && reportData) {
        setReportPreferences({
          id: reportData.id,
          email_reports_enabled: reportData.email_reports_enabled,
          report_frequency: reportData.report_frequency as "weekly" | "monthly",
          report_day: reportData.report_day,
          report_email: reportData.report_email,
          last_report_sent_at: reportData.last_report_sent_at,
        });
      }

      // Fetch notification preferences
      const { data: notifData, error: notifError } = await supabase
        .from("merchant_notification_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      if (!notifError && notifData) {
        setNotificationPreferences({
          id: notifData.id,
          notify_new_order: notifData.notify_new_order,
          notify_payment_received: notifData.notify_payment_received,
          notify_order_completed: notifData.notify_order_completed,
          notify_order_expired: notifData.notify_order_expired,
          notify_refund_requested: notifData.notify_refund_requested,
          notify_low_participation: notifData.notify_low_participation,
          digest_mode: notifData.digest_mode as 'instant' | 'daily' | 'weekly',
          digest_time: notifData.digest_time?.slice(0, 5) || '09:00',
          notification_email: notifData.notification_email,
          notification_phone: notifData.notification_phone,
          whatsapp_enabled: notifData.whatsapp_enabled,
          reminder_enabled: notifData.reminder_enabled ?? true,
          daily_reminder_enabled: (notifData as any).daily_reminder_enabled ?? false,
          one_hour_reminder_enabled: (notifData as any).one_hour_reminder_enabled ?? true,
          additional_reminder_enabled: (notifData as any).additional_reminder_enabled ?? false,
          additional_reminder_hours: (notifData as any).additional_reminder_hours ?? 12,
        });
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveReportPreferences = async () => {
    setSavingReportPrefs(true);
    try {
      const payload = {
        merchant_id: merchantId,
        email_reports_enabled: reportPreferences.email_reports_enabled,
        report_frequency: reportPreferences.report_frequency,
        report_day: reportPreferences.report_day,
        report_email: reportPreferences.report_email || null,
      };

      if (reportPreferences.id) {
        const { error } = await supabase
          .from("merchant_report_preferences")
          .update(payload)
          .eq("id", reportPreferences.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("merchant_report_preferences")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setReportPreferences(prev => ({ ...prev, id: data.id }));
      }

      toast.success(isProfessional ? "Report settings saved!" : "Report preferences saved!");
    } catch (error: any) {
      console.error("Error saving report preferences:", error);
      toast.error("Failed to save report preferences");
    } finally {
      setSavingReportPrefs(false);
    }
  };

  const saveNotificationPreferences = async () => {
    setSavingNotificationPrefs(true);
    try {
      const payload = {
        merchant_id: merchantId,
        notify_new_order: notificationPreferences.notify_new_order,
        notify_payment_received: notificationPreferences.notify_payment_received,
        notify_order_completed: notificationPreferences.notify_order_completed,
        notify_order_expired: notificationPreferences.notify_order_expired,
        notify_refund_requested: notificationPreferences.notify_refund_requested,
        notify_low_participation: notificationPreferences.notify_low_participation,
        digest_mode: notificationPreferences.digest_mode,
        digest_time: notificationPreferences.digest_time + ':00',
        notification_email: notificationPreferences.notification_email || null,
        notification_phone: notificationPreferences.notification_phone || null,
        whatsapp_enabled: notificationPreferences.whatsapp_enabled,
        reminder_enabled: notificationPreferences.reminder_enabled,
        daily_reminder_enabled: notificationPreferences.daily_reminder_enabled,
        one_hour_reminder_enabled: notificationPreferences.one_hour_reminder_enabled,
        additional_reminder_enabled: notificationPreferences.additional_reminder_enabled,
        additional_reminder_hours: notificationPreferences.additional_reminder_hours,
      } as any;

      if (notificationPreferences.id) {
        const { error } = await supabase
          .from("merchant_notification_preferences")
          .update(payload)
          .eq("id", notificationPreferences.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("merchant_notification_preferences")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setNotificationPreferences(prev => ({ ...prev, id: data.id }));
      }

      toast.success(isProfessional ? "Alert preferences saved!" : "Notification preferences saved!");
    } catch (error: any) {
      console.error("Error saving notification preferences:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  const sendTestReminder = async (reminderType: 'midpoint' | 'final') => {
    setSendingTestReminder(reminderType);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-test-reminder`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ merchant_id: merchantId, reminder_type: reminderType }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send test reminder");
      }

      toast.success(isProfessional 
        ? `Test payment reminder sent to ${result.email}!`
        : `Test ${reminderType === 'final' ? 'final' : 'first'} reminder sent to ${result.email}!`);
    } catch (error: any) {
      console.error("Error sending test reminder:", error);
      toast.error(error.message || "Failed to send test reminder");
    } finally {
      setSendingTestReminder(null);
    }
  };

  const sendTestReport = async () => {
    setSendingTestReport(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-analytics-report`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ merchant_id: merchantId, force: true }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send report");
      }

      toast.success("Test report sent! Check your email.");
    } catch (error: any) {
      console.error("Error sending test report:", error);
      toast.error(error.message || "Failed to send test report");
    } finally {
      setSendingTestReport(false);
    }
  };

  const handleSyncMetafieldAccess = async () => {
    setSyncingMetafield(true);
    try {
      const { data, error } = await supabase.functions.invoke("fix-metafield-access", {
        body: { merchantId },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message || "Metafield storefront access synced!");
      } else {
        toast.error(data.message || "Failed to sync metafield access");
      }
    } catch (error: any) {
      console.error("Error syncing metafield access:", error);
      toast.error(error.message || "Failed to sync metafield access");
    } finally {
      setSyncingMetafield(false);
    }
  };

  // Team management functions
  const fetchTeamData = async () => {
    try {
      const { data: members, error: membersError } = await supabase
        .from('merchant_users')
        .select('id, user_id, role, created_at')
        .eq('merchant_id', merchantId);

      if (membersError) throw membersError;

      const { data: invites, error: invitesError } = await supabase
        .from('merchant_invitations')
        .select('id, email, role, status, created_at, expires_at')
        .eq('merchant_id', merchantId)
        .eq('status', 'pending');

      if (invitesError) throw invitesError;

      setTeamMembers(members || []);
      setInvitations(invites || []);
    } catch (error: any) {
      console.error("Error fetching team data:", error);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !inviteRole) {
      toast.error("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSendingInvite(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          merchantId,
          email: inviteEmail.toLowerCase().trim(),
          role: inviteRole
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("view_only");
      setInviteDialogOpen(false);
      fetchTeamData();
    } catch (error: any) {
      console.error("Error sending invite:", error);
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setRevokingInviteId(invitationId);
    try {
      const { error } = await supabase
        .from('merchant_invitations')
        .update({ status: 'revoked' })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success("Invitation revoked");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error revoking invitation:", error);
      toast.error("Failed to revoke invitation");
    } finally {
      setRevokingInviteId(null);
    }
  };

  const handleRemoveMember = async (memberId: string, role: string) => {
    if (role === 'super_admin') {
      toast.error(`Cannot remove the ${ownerLabel.toLowerCase()}`);
      return;
    }

    setRemovingMemberId(memberId);
    try {
      const { error } = await supabase
        .from('merchant_users')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast.success("Team member removed");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove team member");
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: string, currentRole: string) => {
    if (currentRole === 'super_admin') {
      toast.error(`Cannot change the ${ownerLabel.toLowerCase()}'s role`);
      return;
    }

    try {
      const { error } = await supabase
        .from('merchant_users')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      toast.success("Role updated successfully");
      fetchTeamData();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Owner';
      case 'manager': return 'Manager';
      case 'view_only': return 'View Only';
      default: return role;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role) {
      case 'super_admin': return 'default';
      case 'manager': return 'secondary';
      default: return 'outline';
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTeamData();
    }
  }, [merchantId, isSuperAdmin]);

  const effectiveEmail = reportPreferences.report_email || merchantEmail || "Not set";
  const scheduleDescription = reportPreferences.report_frequency === "weekly"
    ? `Every ${DAYS_OF_WEEK.find(d => d.value === reportPreferences.report_day)?.label || "Monday"}`
    : `On the ${DAYS_OF_MONTH.find(d => d.value === reportPreferences.report_day)?.label || "1st"} of each month`;

  const handleSave = async (field: keyof PreferenceData, value: any) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({ [field]: value })
        .eq('id', merchantId);

      if (error) throw error;
      
      setPreferences(prev => ({ ...prev, [field]: value }));
      toast.success("Preference saved");
    } catch (error: any) {
      console.error("Error saving preference:", error);
      toast.error("Failed to save preference");
    } finally {
      setSaving(false);
    }
  };

  const handlePositionChange = async (value: string) => {
    setPreferences(prev => ({ ...prev, widget_position: value }));
    await handleSave('widget_position', value);
  };

  const handleInstallWidget = async () => {
    setInstallingWidget(true);
    try {
      const { data, error } = await supabase.functions.invoke('install-widget', {
        body: { 
          merchantId,
          position: preferences.widget_position,
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        setPreferences(prev => ({ ...prev, widget_installed: true }));
        toast.success("Widget installed successfully!");
      } else {
        toast.error(data?.error || "Failed to install widget");
      }
    } catch (error: any) {
      console.error("Error installing widget:", error);
      toast.error(error.message || "Failed to install widget");
    } finally {
      setInstallingWidget(false);
    }
  };

  const fetchUninstallStats = async () => {
    setLoadingStats(true);
    try {
      const { data: requests, error } = await supabase
        .from('split_payment_requests')
        .select('status, total_price')
        .eq('merchant_id', merchantId);

      if (error) throw error;

      const stats: UninstallStats = {
        totalRequests: requests?.length || 0,
        completedRequests: requests?.filter(r => r.status === 'completed').length || 0,
        pendingRequests: requests?.filter(r => r.status === 'pending').length || 0,
        totalRevenue: requests?.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
        pendingRevenue: requests?.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
      };

      setUninstallStats(stats);
    } catch (error) {
      console.error("Error fetching uninstall stats:", error);
      setUninstallStats({
        totalRequests: 0,
        completedRequests: 0,
        pendingRequests: 0,
        totalRevenue: 0,
        pendingRevenue: 0,
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleUninstallClick = async () => {
    await fetchUninstallStats();
    setShowUninstallDialog(true);
  };

  const handleUninstallWidget = async () => {
    setUninstallingWidget(true);
    try {
      const { data, error } = await supabase.functions.invoke('install-widget', {
        body: { 
          merchantId,
          action: 'uninstall',
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        setPreferences(prev => ({ ...prev, widget_installed: false }));
        toast.success("Widget uninstalled successfully");
        setShowUninstallDialog(false);
      } else {
        toast.error(data?.error || "Failed to uninstall widget");
      }
    } catch (error: any) {
      console.error("Error uninstalling widget:", error);
      toast.error(error.message || "Failed to uninstall widget");
    } finally {
      setUninstallingWidget(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Accordion type="multiple" defaultValue={isProfessional ? ["payment"] : ["widget"]} className="space-y-3">
        {/* Widget & Integration - Hidden for professionals */}
        {!isProfessional && (
        <AccordionItem value="widget" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Code className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">Widget Placement</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Position: {preferences.widget_position === 'image-top-left' ? 'Top Left of Image' : 'Next to Product Title'}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {/* Button Position Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Choose Button Position</Label>
              <p className="text-xs text-muted-foreground">
                Select where the Split Payment button appears on your product pages
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Top Left of Image Option */}
                <button
                  type="button"
                  onClick={() => canEdit && handlePositionChange('image-top-left')}
                  disabled={!canEdit}
                  className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                    preferences.widget_position === 'image-top-left'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 bg-muted/30'
                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {/* Visual Preview */}
                  <div className="flex gap-3 mb-3">
                    <div className="relative w-16 h-20 bg-muted rounded border">
                      <div className="absolute top-1 left-1 w-6 h-3 bg-primary rounded-sm flex items-center justify-center">
                        <span className="text-[6px] text-primary-foreground font-medium">SPLIT</span>
                      </div>
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="h-2 w-16 bg-muted rounded" />
                      <div className="h-2 w-12 bg-muted rounded" />
                      <div className="h-2 w-8 bg-muted rounded" />
                    </div>
                  </div>
                  
                  <p className="text-sm font-medium">Top Left of Image</p>
                  <p className="text-xs text-muted-foreground">Button overlays the product image</p>
                  
                  {preferences.widget_position === 'image-top-left' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                  )}
                </button>

                {/* Next to Title Option */}
                <button
                  type="button"
                  onClick={() => canEdit && handlePositionChange('product-title-next')}
                  disabled={!canEdit}
                  className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                    preferences.widget_position === 'product-title-next'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 bg-muted/30'
                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {/* Visual Preview */}
                  <div className="flex gap-3 mb-3">
                    <div className="w-16 h-20 bg-muted rounded border" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-12 bg-muted rounded" />
                        <div className="w-6 h-3 bg-primary rounded-sm flex items-center justify-center">
                          <span className="text-[6px] text-primary-foreground font-medium">SPLIT</span>
                        </div>
                      </div>
                      <div className="h-2 w-10 bg-muted rounded" />
                      <div className="h-2 w-8 bg-muted rounded" />
                    </div>
                  </div>
                  
                  <p className="text-sm font-medium">Next to Product Title</p>
                  <p className="text-xs text-muted-foreground">Button appears inline with title</p>
                  
                  {preferences.widget_position === 'product-title-next' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* Sync Status & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </Badge>
                <span className="text-xs text-muted-foreground">Widget synced with your store</span>
              </div>
              
              {canEdit && (
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleSyncMetafieldAccess} 
                    disabled={syncingMetafield}
                    title="Fixes products not showing on store by enabling Storefront API access"
                  >
                    {syncingMetafield ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Shield className="h-3 w-3 mr-1" />}
                    Fix Storefront Access
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleInstallWidget} disabled={installingWidget}>
                    {installingWidget ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    Resync
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => window.open('/merchant/widget-guide', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Advanced Setup
                  </Button>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        )}

        {/* Payment Deadline */}
        <AccordionItem value="payment" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">{isProfessional ? "Payment Deadline" : "Payment & Cancellation"}</p>
                <p className="text-xs text-muted-foreground font-normal">
                  {isProfessional 
                    ? `Default: ${preferences.default_deadline_hours && preferences.default_deadline_hours >= 24 
                        ? `${Math.floor(preferences.default_deadline_hours / 24)} day${Math.floor(preferences.default_deadline_hours / 24) > 1 ? 's' : ''}`
                        : `${preferences.default_deadline_hours}h`}`
                    : `Deadline: ${preferences.default_deadline_hours}h • Cancel fee: ${preferences.cancellation_fee_type === 'fixed' ? formatCurrency(preferences.cancellation_fee_value ?? 100) : `${preferences.cancellation_fee_value}%`}`
                  }
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {/* Payment Deadline */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground">Payment Deadline</Label>
              </div>
              <Select
                value={String(preferences.default_deadline_hours)}
                onValueChange={(value) => handleSave('default_deadline_hours', parseInt(value))}
                disabled={!canEdit || saving}
              >
                <SelectTrigger className="h-9 max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isProfessional ? (
                    <>
                      <SelectItem value="24">1 day</SelectItem>
                      <SelectItem value="48">2 days</SelectItem>
                      <SelectItem value="72">3 days</SelectItem>
                      <SelectItem value="120">5 days</SelectItem>
                      <SelectItem value="168">7 days</SelectItem>
                      <SelectItem value="336">14 days</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="24">24 hours (1 day)</SelectItem>
                      <SelectItem value="48">48 hours (2 days)</SelectItem>
                      <SelectItem value="72">72 hours (3 days)</SelectItem>
                      <SelectItem value="120">120 hours (5 days)</SelectItem>
                      <SelectItem value="168">168 hours (7 days)</SelectItem>
                      <SelectItem value="336">336 hours (14 days)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Currency Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Wallet className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground">Store Currency</Label>
              </div>
              <Select
                value={preferences.currency}
                onValueChange={(value) => {
                  setPreferences(prev => ({ ...prev, currency: value }));
                  handleSave('currency', value);
                }}
                disabled={!canEdit || saving}
              >
                <SelectTrigger className="h-9 max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ INR - Indian Rupee</SelectItem>
                  <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                  <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                  <SelectItem value="AUD">$ AUD - Australian Dollar</SelectItem>
                  <SelectItem value="CAD">$ CAD - Canadian Dollar</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Currency used in emails and payment notifications.
              </p>
            </div>

            {!isProfessional && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
                <div className="flex items-center gap-2">
                  <Ban className="h-3 w-3 text-muted-foreground" />
                  <Label className="text-xs font-medium">Cancellation Fee</Label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select
                      value={preferences.cancellation_fee_type || 'fixed'}
                      onValueChange={(value: 'fixed' | 'percentage') => {
                        setPreferences(prev => ({ ...prev, cancellation_fee_type: value }));
                        handleSave('cancellation_fee_type', value);
                      }}
                      disabled={!canEdit || saving}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {preferences.cancellation_fee_type === 'percentage' ? 'Percentage' : 'Amount'}
                    </Label>
                    <div className="flex gap-2 items-center">
                      {preferences.cancellation_fee_type !== 'percentage' && <span className="text-sm text-muted-foreground">{getCurrencySymbol()}</span>}
                      <Input
                        type="number"
                        min="0"
                        max={preferences.cancellation_fee_type === 'percentage' ? 100 : 10000}
                        step={preferences.cancellation_fee_type === 'percentage' ? 0.5 : 10}
                        value={preferences.cancellation_fee_value ?? 100}
                        onChange={(e) => setPreferences(prev => ({ ...prev, cancellation_fee_value: parseFloat(e.target.value) || 0 }))}
                        onBlur={(e) => handleSave('cancellation_fee_value', parseFloat(e.target.value) || 0)}
                        className="w-20 h-9"
                        disabled={!canEdit}
                      />
                      {preferences.cancellation_fee_type === 'percentage' && <span className="text-sm text-muted-foreground">%</span>}
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Your share: <span className="font-medium text-foreground">
                    {preferences.cancellation_fee_type === 'fixed' 
                      ? formatCurrency((preferences.cancellation_fee_value ?? 100) / 2)
                      : `${((preferences.cancellation_fee_value ?? 0) / 2).toFixed(1)}%`
                    }
                  </span> (50% split with Shakatah)
                </p>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Invoice Settings - Professional Only */}
        {isProfessional && (
          <AccordionItem value="invoices" className="border rounded-lg px-4 bg-card">
            <AccordionTrigger className="hover:no-underline py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <FileText className="h-4 w-4 text-cyan-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Invoice Generation</p>
                  <p className="text-xs text-muted-foreground font-normal">
                    Auto-generate invoices with payment requests
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <InvoiceSettingsForm merchantId={merchantId} />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Payment Reminders */}
        <AccordionItem value="reminders" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BellRing className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium">Payment Reminders</p>
                <p className="text-xs text-muted-foreground font-normal">
                  {notificationPreferences.reminder_enabled ? 'Enabled' : 'Disabled'} • {notificationPreferences.daily_reminder_enabled ? 'Daily reminders on' : 'Daily reminders off'}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              {isProfessional 
                ? `Configure automated reminders sent to ${customersLabel.toLowerCase()} about pending payments`
                : 'Configure automated reminders sent to participants and organizers about pending payments'
              }
            </p>
            
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
              <div className="flex items-center gap-2">
                <BellRing className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">Enable Payment Reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    {isProfessional 
                      ? `Automatically remind ${customersLabel.toLowerCase()} who haven't paid`
                      : "Automatically remind participants who haven't paid"
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationPreferences.reminder_enabled}
                onCheckedChange={(checked) => 
                  setNotificationPreferences(prev => ({ ...prev, reminder_enabled: checked }))
                }
                disabled={!canEdit}
                className={isProfessional ? "data-[state=checked]:bg-cyan-500" : ""}
              />
            </div>
            
            {notificationPreferences.reminder_enabled && (
              <div className="space-y-4 pt-3 border-t">
                {/* Immediate reminder - always on */}
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-green-700 dark:text-green-400">Immediate Notification</Label>
                    <p className="text-xs text-green-600 dark:text-green-500">
                      {isProfessional 
                        ? "Sent automatically when payment request is created"
                        : "Sent automatically when order is created"
                      }
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                    Always On
                  </Badge>
                </div>
                
                {/* Daily reminder toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="text-sm font-medium">Every 24 Hours</Label>
                      <p className="text-xs text-muted-foreground">Send reminder daily until payment or deadline</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationPreferences.daily_reminder_enabled}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({ ...prev, daily_reminder_enabled: checked }))
                    }
                    disabled={!canEdit}
                    className={isProfessional ? "data-[state=checked]:bg-cyan-500" : ""}
                  />
                </div>
                
                {/* One hour before expiry toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <Label className="text-sm font-medium text-amber-700 dark:text-amber-400">1 Hour Before Expiry</Label>
                      <p className="text-xs text-amber-600 dark:text-amber-500">Urgent reminder before deadline</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationPreferences.one_hour_reminder_enabled}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({ ...prev, one_hour_reminder_enabled: checked }))
                    }
                    disabled={!canEdit}
                    className={isProfessional ? "data-[state=checked]:bg-cyan-500" : ""}
                  />
                </div>
                
                {/* Optional additional reminder */}
                <div className="p-4 rounded-lg border border-dashed bg-muted/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      <Label className="text-sm font-medium">Add Custom Reminder</Label>
                    </div>
                    <Switch
                      checked={notificationPreferences.additional_reminder_enabled}
                      onCheckedChange={(checked) => 
                        setNotificationPreferences(prev => ({ ...prev, additional_reminder_enabled: checked }))
                      }
                      disabled={!canEdit}
                      className={isProfessional ? "data-[state=checked]:bg-cyan-500" : ""}
                    />
                  </div>
                  
                  {notificationPreferences.additional_reminder_enabled && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Reminder Timing</Label>
                      <Select
                        value={String(notificationPreferences.additional_reminder_hours)}
                        onValueChange={(value) => 
                          setNotificationPreferences(prev => ({ ...prev, additional_reminder_hours: Number(value) }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">At midpoint</SelectItem>
                          <SelectItem value="6">6 hours before deadline</SelectItem>
                          <SelectItem value="12">12 hours before deadline</SelectItem>
                          <SelectItem value="36">36 hours before deadline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    {notificationPreferences.additional_reminder_enabled 
                      ? "An additional reminder will be sent at the configured time"
                      : "Enable to add an extra reminder between your scheduled ones"}
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Reminders are sent via email to {isProfessional ? customersLabel.toLowerCase() : 'participants'} who haven't completed payment
                </p>
                
                {/* Email Preview */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <EmailPreviewDialog 
                    storeName={preferences.store_name || 'Your Store'} 
                    brandColor={preferences.brand_color || '#8B5CF6'} 
                  />
                </div>
              </div>
            )}

            <Button 
              size="sm" 
              onClick={saveNotificationPreferences} 
              disabled={savingNotificationPrefs || !canEdit}
              className={isProfessional ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white' : ''}
            >
              {savingNotificationPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
              Save Reminder Settings
            </Button>
          </AccordionContent>
        </AccordionItem>

        {/* Team Management - Only for Super Admins */}
        {isSuperAdmin && (
          <AccordionItem value="team" className="border rounded-lg px-4 bg-card">
            <AccordionTrigger className="hover:no-underline py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium">Team Management</p>
                  <p className="text-xs text-muted-foreground font-normal">
                    {teamMembers.length > 0 
                      ? `${teamMembers.length} member${teamMembers.length !== 1 ? 's' : ''} (including you)`
                      : '1 member (you)'
                    } • {invitations.length} pending invite{invitations.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4 space-y-4">
              {/* Team Members */}
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Team Members</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTeamData}>
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <UserPlus className="h-3 w-3 mr-2" />
                        Invite
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                        <DialogDescription>
                          Send an invitation to join your store's team
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="invite-email">Email Address</Label>
                          <Input
                            id="invite-email"
                            type="email"
                            placeholder="teammate@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="invite-role">Role</Label>
                          <Select value={inviteRole} onValueChange={setInviteRole}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manager">
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">Manager</span>
                                  <span className="text-xs text-muted-foreground">Can manage payments, refunds, and view all data</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="view_only">
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">View Only</span>
                                  <span className="text-xs text-muted-foreground">Can only view data, no actions allowed</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSendInvite} disabled={sendingInvite}>
                          {sendingInvite ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="h-4 w-4 mr-2" />
                              Send Invitation
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Member List */}
              <div className="space-y-2">
                {/* Always show the owner (super_admin) first */}
                {teamMembers.filter(m => m.role === 'super_admin').length > 0 ? (
                  teamMembers.filter(m => m.role === 'super_admin').map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Shield className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {ownerLabel} <span className="text-xs text-muted-foreground">(You)</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-primary text-primary-foreground">
                        Owner
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {ownerLabel} <span className="text-xs text-muted-foreground">(You)</span>
                        </p>
                        <p className="text-xs text-muted-foreground">Primary admin</p>
                      </div>
                    </div>
                    <Badge className="bg-primary text-primary-foreground">
                      Owner
                    </Badge>
                  </div>
                )}
                
                {/* Other team members */}
                {teamMembers.filter(m => m.role !== 'super_admin').map((member) => {
                  const RoleIcon = member.role === 'manager' ? Settings2 : Eye;
                  
                  return (
                    <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border bg-background">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <RoleIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Team Member</p>
                          <p className="text-xs text-muted-foreground">
                            Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getRoleBadgeVariant(member.role)}>
                          {getRoleLabel(member.role)}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.role !== 'manager' && (
                              <DropdownMenuItem onClick={() => handleUpdateMemberRole(member.id, 'manager', member.role)}>
                                <Settings2 className="h-4 w-4 mr-2" />
                                Make Manager
                              </DropdownMenuItem>
                            )}
                            {member.role !== 'view_only' && (
                              <DropdownMenuItem onClick={() => handleUpdateMemberRole(member.id, 'view_only', member.role)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Make View Only
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleRemoveMember(member.id, member.role)}
                              disabled={removingMemberId === member.id}
                            >
                              {removingMemberId === member.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Pending Invitations</Label>
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{invitation.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Expires {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{getRoleLabel(invitation.role)}</Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleRevokeInvitation(invitation.id)}
                          disabled={revokingInviteId === invitation.id}
                        >
                          {revokingInviteId === invitation.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Communication & Branding */}
        <AccordionItem value="communication" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Palette className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left flex items-center gap-3">
                <div>
                  <p className="font-medium">Communication & Branding</p>
                  <p className="text-xs text-muted-foreground font-normal">Email reports, notifications & brand styling</p>
                </div>
                <div 
                  className="w-5 h-5 rounded-full border-2 border-background shadow-sm" 
                  style={{ backgroundColor: preferences.brand_color || '#8B5CF6' }}
                />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            {/* Business/Store Name in Emails */}
            <div className="p-3 rounded-lg border bg-muted/30 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">{storeLabel} Name in Emails</Label>
                <p className="text-xs text-muted-foreground">
                  {isProfessional 
                    ? `How your ${storeLabel.toLowerCase()} name appears in ${customersLabel.toLowerCase()} emails (e.g., "Payment request from <strong>Your ${storeLabel}</strong>")`
                    : `How your store name appears in customer emails (e.g., "Your order from <strong>StoreName</strong> via Shakatah")`
                  }
                </p>
              </div>
              <Input
                type="text"
                placeholder={preferences.store_name || `Your ${storeLabel} Name`}
                value={preferences.email_store_name || ''}
                onChange={(e) => setPreferences(prev => ({ ...prev, email_store_name: e.target.value }))}
                onBlur={(e) => handleSave('email_store_name', e.target.value || null)}
                className="h-9"
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use your {storeLabel.toLowerCase()} name: <strong>{preferences.store_name || storeDomain}</strong>
              </p>
            </div>

            {/* Brand Color */}
            <div className="p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Brand Color</Label>
                  <p className="text-xs text-muted-foreground">
                    {isProfessional ? "Used in emails & payment pages" : "Used in emails, payment pages & widget"}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={preferences.brand_color || '#8B5CF6'}
                    onChange={(e) => setPreferences(prev => ({ ...prev, brand_color: e.target.value }))}
                    onBlur={(e) => handleSave('brand_color', e.target.value)}
                    className="w-10 h-9 rounded-lg border cursor-pointer"
                    disabled={!canEdit}
                  />
                  <Input
                    type="text"
                    value={preferences.brand_color || '#8B5CF6'}
                    onChange={(e) => setPreferences(prev => ({ ...prev, brand_color: e.target.value }))}
                    onBlur={(e) => handleSave('brand_color', e.target.value)}
                    className="w-24 h-9 font-mono text-xs"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>

            {/* Email Reports Settings */}
            <div className="p-3 rounded-lg border bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${reportPreferences.email_reports_enabled ? "bg-green-500/10" : "bg-muted"}`}>
                    <Mail className={`h-4 w-4 ${reportPreferences.email_reports_enabled ? "text-green-500" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Email Reports</Label>
                    <p className="text-xs text-muted-foreground">
                      {reportPreferences.email_reports_enabled ? scheduleDescription : "Currently disabled"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={reportPreferences.email_reports_enabled}
                  onCheckedChange={(checked) => 
                    setReportPreferences(prev => ({ ...prev, email_reports_enabled: checked }))
                  }
                  disabled={!canEdit}
                />
              </div>

              {reportPreferences.email_reports_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Frequency
                      </Label>
                      <Select
                        value={reportPreferences.report_frequency}
                        onValueChange={(value: "weekly" | "monthly") => 
                          setReportPreferences(prev => ({ 
                            ...prev, 
                            report_frequency: value,
                            report_day: value === "weekly" ? 1 : 1
                          }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {reportPreferences.report_frequency === "weekly" ? "Day of Week" : "Day of Month"}
                      </Label>
                      <Select
                        value={reportPreferences.report_day.toString()}
                        onValueChange={(value) => 
                          setReportPreferences(prev => ({ ...prev, report_day: parseInt(value) }))
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {reportPreferences.report_frequency === "weekly"
                            ? DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={day.value.toString()}>
                                  {day.label}
                                </SelectItem>
                              ))
                            : DAYS_OF_MONTH.map((day) => (
                                <SelectItem key={day.value} value={day.value.toString()}>
                                  {day.label}
                                </SelectItem>
                              ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="p-2 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                      <p className="text-xs text-muted-foreground">
                        A {reportPreferences.report_frequency} report will be sent to{" "}
                        <span className="font-medium text-foreground">{merchantEmail || "store admin email"}</span>{" "}
                        {scheduleDescription.toLowerCase()}. Add team members in the Team tab to include others.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={saveReportPreferences} 
                      disabled={savingReportPrefs || !canEdit}
                      className={isProfessional ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white' : ''}
                    >
                      {savingReportPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                      Save
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={sendTestReport} 
                      disabled={sendingTestReport || !canEdit}
                      className={isProfessional ? 'border-cyan-300 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-700 dark:text-cyan-400 dark:hover:bg-cyan-950' : ''}
                    >
                      {sendingTestReport ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Send className="h-3 w-3 mr-2" />}
                      Send Test
                    </Button>
                  </div>
                </>
              )}

              {!reportPreferences.email_reports_enabled && (
                <Button 
                  size="sm" 
                  onClick={saveReportPreferences} 
                  disabled={savingReportPrefs || !canEdit}
                  className={isProfessional ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white' : ''}
                >
                  {savingReportPrefs ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
                  Save
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {!isProfessional && (
        <Alert className="bg-muted/30 border-muted">
          <Zap className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Changes save automatically and apply to new split payment orders.
          </AlertDescription>
        </Alert>
      )}

    </div>
  );
};

export default MerchantPreferences;